/**
 * Screen-data supplier: lets the page under the Ask-Moo panel contribute
 * fresh, capture-at-SEND data into the outgoing screen context's entityData.
 *
 * Why not put this data in the screen-context chip itself: chips are built on
 * route change, but form state (edit-quote sliders) changes without a route
 * change — a chip snapshot would go stale the moment a slider moves. The
 * supplier is invoked inside BOTH chat shells' request builders (V1
 * useAskMooChat transport and V2 cowbell-chat-transport), so the values are
 * read at the instant the message is sent regardless of which shell is live.
 *
 * Module-level singleton on purpose: there is exactly one Ask-Moo panel and
 * at most one supplier page mounted at a time. Registering pages MUST clear
 * the supplier on unmount (useScreenDataSupplier does this) so a stale
 * supplier can never outlive its page.
 */
import { useEffect, useRef } from 'react';

export interface SuppliedScreenData {
  /**
   * The screen chip this data belongs to. The merge attaches the data ONLY to
   * the screen context whose entityId matches — never to pinned chips from
   * other screens riding along in the same message.
   */
  entityId: string;
  data: Record<string, unknown>;
}

export type ScreenDataSupplier = () => SuppliedScreenData | null;

let supplier: ScreenDataSupplier | null = null;

export function setScreenDataSupplier(fn: ScreenDataSupplier | null): void {
  supplier = fn;
}

/** Invoke the registered supplier; never throws (a broken page must not block sending). */
export function getScreenData(): SuppliedScreenData | null {
  if (!supplier) return null;
  try {
    return supplier();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[ask-moo] screen data supplier failed', err);
    return null;
  }
}

/**
 * Minimal structural shape the merge needs. V1 passes ChatContext[] (type:
 * 'screen'); V2 passes already-wire-shaped records (also type: 'screen' after
 * toWireContext). Both carry entityId/entityData on screen chips.
 */
interface ScreenLikeContext {
  type?: unknown;
  entityId?: unknown;
  entityData?: Record<string, unknown>;
}

/**
 * Merge capture-at-send page data into the ONE screen chip it belongs to
 * (matched by entityId). Other contexts — entity chips, documents, pinned
 * screen chips from other pages — pass through untouched. If the user
 * dismissed the matching screen chip there is nothing to attach: that is the
 * correct consent semantic.
 */
export function mergeScreenDataIntoContexts<T extends ScreenLikeContext>(
  contexts: T[],
  supplied: SuppliedScreenData | null
): T[] {
  if (!supplied) return contexts;
  return contexts.map((c) =>
    c.type === 'screen' && c.entityId === supplied.entityId
      ? { ...c, entityData: { ...c.entityData, ...supplied.data } }
      : c
  );
}

/**
 * Register a page as the active screen-data supplier for its mounted
 * lifetime. The supplier reads through a ref so it always sees the latest
 * render's state without re-registering, and it is cleared on unmount so it
 * can never leak onto another page.
 */
export function useScreenDataSupplier(supplierFn: ScreenDataSupplier): void {
  const ref = useRef(supplierFn);
  ref.current = supplierFn;
  useEffect(() => {
    setScreenDataSupplier(() => ref.current());
    return () => setScreenDataSupplier(null);
  }, []);
}
